/**
 * DocumentApiService
 * Handles all document-related API endpoints
 */

import type { HttpClient } from './HttpClient';
import type {
  Document,
  DocumentCreate,
  SearchRequest,
  SearchResponse,
  BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut,
} from '../../api/data-contracts';

/**
 * Document pagination parameters
 */
export interface DocumentPaginationParams {
  skip?: number;
  limit?: number;
  category?: string;
  source_type?: string;
}

/**
 * Document upload options
 */
export interface DocumentUploadOptions {
  title?: string;
  permissions?: Partial<BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut>;
  textExtractionProvider?: string;
}

/**
 * Service for document operations
 */
export class DocumentApiService {
  constructor(private readonly httpClient: HttpClient) {}

  /**
   * Helper to build FormData with permission fields
   * Eliminates code duplication across upload/update/permissions methods
   */
  private buildPermissionsFormData(
    permissions: Partial<BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut>
  ): FormData {
    const formData = new FormData();

    if (permissions.visibility) {
      formData.append('visibility', permissions.visibility);
    }
    if (permissions.purpose) {
      formData.append('purpose', permissions.purpose);
    }
    if (permissions.shared_with_users) {
      formData.append('shared_with_users', permissions.shared_with_users);
    }
    if (permissions.shared_with_groups) {
      formData.append('shared_with_groups', permissions.shared_with_groups);
    }
    if (permissions.shared_with_roles) {
      formData.append('shared_with_roles', permissions.shared_with_roles);
    }
    if (permissions.retention_days !== undefined) {
      formData.append('retention_days', permissions.retention_days.toString());
    }

    return formData;
  }

  /**
   * Search documents
   */
  public async search(
    query: string,
    filters: Record<string, unknown> = {},
    limit: number = 10
  ): Promise<SearchResponse> {
    const searchRequest: SearchRequest = {
      query,
      filter: filters,
      limit,
    };

    return this.httpClient.post<SearchResponse>('/documents/search', searchRequest);
  }

  /**
   * Get paginated list of documents
   */
  public async getDocuments(params: DocumentPaginationParams = {}): Promise<Document[]> {
    const { skip = 0, limit = 20, category, source_type } = params;

    const queryParams: Record<string, string | number> = {
      skip,
      limit,
    };

    if (category) {
      queryParams.category = category;
    }

    if (source_type) {
      queryParams.source_type = source_type;
    }

    return this.httpClient.get<Document[]>('/documents', { params: queryParams });
  }

  /**
   * Get a single document by ID
   */
  public async getDocument(documentId: string): Promise<Document> {
    return this.httpClient.get<Document>(`/documents/${documentId}`);
  }

  /**
   * Create a new document
   */
  public async createDocument(document: DocumentCreate): Promise<Document> {
    return this.httpClient.post<Document>('/documents', document);
  }

  /**
   * Upload a document file
   */
  public async uploadDocument(
    file: File,
    options: DocumentUploadOptions = {}
  ): Promise<Document> {
    // Use helper to build FormData with permissions
    const formData = options.permissions
      ? this.buildPermissionsFormData(options.permissions)
      : new FormData();

    formData.append('file', file);

    if (options.title) {
      formData.append('title', options.title);
    }

    if (options.textExtractionProvider) {
      formData.append('text_extraction_provider', options.textExtractionProvider);
    }

    return this.httpClient.post<Document>('/documents/upload', formData);
  }

  /**
   * Update a document file
   */
  public async updateDocumentFile(
    documentId: string,
    file: File,
    options: DocumentUploadOptions = {}
  ): Promise<Document> {
    // Use helper to build FormData with permissions
    const formData = options.permissions
      ? this.buildPermissionsFormData(options.permissions)
      : new FormData();

    formData.append('file', file);

    if (options.title) {
      formData.append('title', options.title);
    }

    return this.httpClient.put<Document>(`/documents/${documentId}/file`, formData);
  }

  /**
   * Update document metadata
   */
  public async updateDocument(documentId: string, updates: Partial<Document>): Promise<Document> {
    return this.httpClient.put<Document>(`/documents/${documentId}`, updates);
  }

  /**
   * Delete a document
   */
  public async deleteDocument(documentId: string): Promise<{ message: string }> {
    return this.httpClient.delete<{ message: string }>(`/documents/${documentId}`);
  }

  /**
   * Get original document blob with response headers
   * Used for downloading and viewing documents
   */
  public async getDocumentBlob(documentId: string, display: boolean = false): Promise<Response> {
    const endpoint = `/documents/${documentId}/original`;
    const params = display ? { display: 'true' } : undefined;
    return this.httpClient.getBlob(endpoint, { params });
  }

  /**
   * Download original document file
   * Uses centralized HTTP client for proper authentication and error handling
   */
  public async downloadDocument(documentId: string): Promise<void> {
    const response = await this.getDocumentBlob(documentId, false);

    // Extract filename from Content-Disposition header
    let filename = 'document';
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      // Try UTF-8 format first (RFC 5987)
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/);
      if (utf8Match?.[1]) {
        filename = decodeURIComponent(utf8Match[1]);
      } else {
        // Fallback to standard format
        const standardMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (standardMatch?.[1]) {
          filename = standardMatch[1];
        }
      }
    }

    // Create blob and download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * Check document access permissions
   */
  public async checkDocumentAccess(documentId: string): Promise<{
    has_access: boolean;
    permissions: string[];
  }> {
    return this.httpClient.get(`/documents/${documentId}/access`);
  }

  /**
   * Update document permissions
   */
  public async updateDocumentPermissions(
    documentId: string,
    permissions: Partial<BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut>
  ): Promise<{ success: boolean; message: string }> {
    const formData = this.buildPermissionsFormData(permissions);
    return this.httpClient.put(`/documents/${documentId}/permissions`, formData);
  }

  /**
   * Revoke user access to document
   */
  public async revokeDocumentAccess(documentId: string): Promise<{ message: string }> {
    return this.httpClient.delete(`/documents/${documentId}/access`);
  }

  /**
   * Get all documents accessible to current user
   */
  public async getMyAccessibleDocuments(): Promise<string[]> {
    return this.httpClient.get<string[]>('/documents/my-documents');
  }
}
